import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { FormInput } from 'components/molecules/FormInput';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useLocation, useSearchParams } from 'react-router-dom';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { EPerPages } from 'models/table';
import { FormAsyncMultiAutocomplete } from 'components/molecules/FormAsyncMultiAutocomplete';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';

export interface ISearchFormValues {
  makesUniqueIds: string[];
  modelsUniqueIds: string[];
  manufacturersUniqueIds: string[];
  calibersUniqueIds: string[];
  productCode: string;
  actionsUniqueIds: string[];
  countriesUniqueIds: string[];
  typesUniqueIds: string[];
  isLocal: number;
  level: string;
  legalClassification: string;
  invokedByPageChange?: boolean;
}

export enum ESearchFormLabels {
  makesUniqueIds = 'firearmsReferenceTable.accordion.make.label',
  modelsUniqueIds = 'firearmsReferenceTable.accordion.model.label',
  manufacturersUniqueIds = 'firearmsReferenceTable.accordion.manufacturer.label',
  calibersUniqueIds = 'firearmsReferenceTable.accordion.calibre.label',
  productCode = 'firearmsReferenceTable.accordion.alsoKnownAsProductCode.label',
  actionsUniqueIds = 'firearmsReferenceTable.accordion.firearmAction.label',
  countriesUniqueIds = 'firearmsReferenceTable.accordion.country.label',
  typesUniqueIds = 'firearmsReferenceTable.accordion.type.label',
  isLocal = 'firearmsReferenceTable.accordion.dataSource.label',
  level = 'firearmsReferenceTable.accordion.level.label',
  legalClassification = 'firearmsReferenceTable.accordion.legalClassification.label',
}

export const initialForm: ISearchFormValues = {
  makesUniqueIds: [],
  modelsUniqueIds: [],
  manufacturersUniqueIds: [],
  calibersUniqueIds: [],
  productCode: '',
  actionsUniqueIds: [],
  countriesUniqueIds: [],
  typesUniqueIds: [],
  isLocal: 0,
  level: '',
  legalClassification: '',
};

interface IFRTSearchForm {
  onSubmit: (arg: any) => void;
  initialFormValues?: ISearchFormValues;
  showSaveMyFilter?: boolean;
  isSearchButtonDisabled?: boolean;
  isTransactionContext?: boolean;
  isSearchForFRTContext?: boolean;
  currentPage?: number;
  perPage?: EPerPages;
  getNewQueryPath?: (arg: any) => string;
}

const FRTSearchForm = ({
  onSubmit,
  initialFormValues = initialForm,
  showSaveMyFilter = true,
  isSearchButtonDisabled = false,
  isTransactionContext = false,
  isSearchForFRTContext = false,
  perPage,
  currentPage,
  getNewQueryPath,
}: IFRTSearchForm) => {
  const [values, setValues] = useState<ISearchFormValues>(initialFormValues);
  const { state: routerState }: any = useLocation();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const [isInitialFormLoad, setIsInitialFormLoad] = useState(true);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = useForm<ISearchFormValues>({
    defaultValues: initialFormValues,
  });
  const [searchParams] = useSearchParams();

  const dataSourceOptions = [
    {
      uniqueId: 0,
      type: 'data_source',
      name: t('frtTable.dataSource.frt.label'),
      isActive: true,
    },
    {
      uniqueId: 1,
      type: 'data_source',
      name: t('frtTable.dataSource.local.label'),
      isActive: true,
    },
  ];

  useEffect(() => {
    if (!isInitialFormLoad) {
      handleSubmit((val) => onSubmit({ ...val, invokedByPageChange: true }))();
    } else {
      setIsInitialFormLoad(false);
    }
  }, [perPage, currentPage]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchFormValues);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const {
    make,
    model,
    caliber,
    manufacturer,
    firearmAction,
    firearmType,
    level,
    manufacturerCountry,
    loadDictionaries,
  } = useLegalEntities();

  useEffect(() => {
    (async function initDictionaries() {
      await Promise.all([
        loadDictionaries([
          EDictionaryTypes.Make,
          EDictionaryTypes.Model,
          EDictionaryTypes.Manufacturer,
          EDictionaryTypes.ManufacturerCountry,
          EDictionaryTypes.FirearmAction,
          EDictionaryTypes.FirearmType,
          EDictionaryTypes.Legality,
          EDictionaryTypes.Caliber,
        ]),
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (routerState?.presetIndividual) {
      handleSubmit(onSubmit)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    setValues(initialFormValues);
    reset(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      const formValues = { ...getValues() };
      Object.keys(formValues).map((key) => {
        // @ts-expect-error: required by update
        if (typeof values[key] === 'string') {
          // @ts-expect-error: required by update
          values[key] = '';
          // @ts-expect-error: required by update
        } else if (typeof values[key] === 'object') {
          // @ts-expect-error: required by update
          values[key] = null;
        }
        return false;
      });
      reset(values);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  useEffect(() => {
    const isLocal = searchParams.get('isLocal');
    setValue('isLocal', isLocal === 'true' ? 1 : 0);
  }, [searchParams]);

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(onSubmit)} id="firearms_reference_table_search_form">
        <Grid container columnSpacing={1}>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Make}
              name="makesUniqueIds"
              label={t(ESearchFormLabels.makesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Model}
              name="modelsUniqueIds"
              label={t(ESearchFormLabels.modelsUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Manufacturer}
              name="manufacturersUniqueIds"
              label={t(ESearchFormLabels.manufacturersUniqueIds)}
              errors={errors}
              control={control}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.ManufacturerCountry}
              name="countriesUniqueIds"
              label={t(ESearchFormLabels.countriesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="productCode"
              label={t(ESearchFormLabels.productCode)}
              setValue={setValue}
              control={control}
              errors={errors}
              noWrap
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(firearmAction)}
              name="actionsUniqueIds"
              setValue={setValue}
              label={t(ESearchFormLabels.actionsUniqueIds)}
              canBeEmpty
              control={control}
              errors={errors}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(firearmType)}
              name="typesUniqueIds"
              label={t(ESearchFormLabels.typesUniqueIds)}
              setValue={setValue}
              canBeEmpty
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormAsyncMultiAutocomplete
              dictionaryType={EDictionaryTypes.Caliber}
              name="calibersUniqueIds"
              label={t(ESearchFormLabels.calibersUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 12 / 5 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(dataSourceOptions)}
              name="isLocal"
              label={t(ESearchFormLabels.isLocal)}
              canBeEmpty={false}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6, lg: 9, xl: 12 / 5 }}
            sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}
          >
            <Grid size={{ xs: 12, lg: 4, xl: 12 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                id="firearms_reference_table-search"
                label={t('firearmsReferenceTable.accordion.search.button')}
                type="submit"
                fullWidth
                disabled={isSearchButtonDisabled}
                sx={{ marginBottom: '23px' }}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
      <SelectedFilters
        name="frt"
        values={values}
        showSaveFilters={showSaveMyFilter}
        labels={ESearchFormLabels}
        handleDelete={handleResetField}
        noPreset
        updatedQueryPath={getNewQueryPath ? getNewQueryPath(getValues()) : undefined}
        saveQueryParams={isTransactionContext ? ['type'] : undefined}
        dictionaries={{
          makesUniqueIds: prepareFilters(make, 'uniqueId', 'name'),
          modelsUniqueIds: prepareFilters(model, 'uniqueId', 'name'),
          manufacturersUniqueIds: prepareFilters(manufacturer, 'uniqueId', 'name'),
          calibersUniqueIds: prepareFilters(caliber, 'uniqueId', 'name'),
          actionsUniqueIds: prepareFilters(firearmAction, 'uniqueId', 'name'),
          countriesUniqueIds: prepareFilters(manufacturerCountry, 'uniqueId', 'name'),
          typesUniqueIds: prepareFilters(firearmType, 'uniqueId', 'name'),
          level: prepareFilters(level, 'uniqueId', 'name'),
          isLocal: prepareFilters(dataSourceOptions, 'uniqueId', 'name'),
        }}
        noRemovable={['isLocal']}
        saveRouterStateAfterClearAll={isSearchForFRTContext}
      />
    </SearchWrapper>
  );
};

export { FRTSearchForm };
